/**
 * <PatientPicker />
 * --------------------------------------------------------------------------
 * Unified patient picker shared across the whole app. Replaces the dozen
 * inline implementations that historically diverged (one used `q=`, others
 * `search=`, some forgot pagination, etc.).
 *
 * Features
 *  - Debounced search against `GET /api/patients?search=...&page_size=25`
 *  - Shows name + DNI + NHC + last-doctor in each suggestion
 *  - Keyboard navigation (↑/↓ to move, Enter to pick, Esc to clear)
 *  - Controlled or uncontrolled `value` (the picked patient)
 *  - Optional “locked” mode after a patient is picked (with Change button)
 *
 * Usage
 *   <PatientPicker
 *     value={pickedPatient}
 *     onChange={setPickedPatient}
 *     placeholder="Buscar paciente…"
 *     autoFocus
 *     testId="export-patient"
 *   />
 *
 * The component intentionally has NO `Card` wrapper — drop it inside any
 * layout. Style of the input/dropdown matches Shadcn/UI.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, User, X, FileText, CalendarClock, Stethoscope } from 'lucide-react';
import { toTitleCaseNameLive } from '@/lib/textUtils';
import api from '@/lib/api';

const MIN_QUERY = 2;
const PAGE_SIZE = 25;
const DEBOUNCE_MS = 250;

const _MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function formatShort(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  // Accept YYYY-MM-DD or full ISO; show just `d MMM yy`
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateStr;
  const yy = m[1].slice(2);
  const mon = _MONTHS_ES[parseInt(m[2], 10) - 1] || '';
  const day = parseInt(m[3], 10);
  return `${day} ${mon} ${yy}`;
}

export default function PatientPicker({
  value = null,
  onChange,
  onSecondaryAction,
  placeholder = 'Buscar por nombre, DNI o NHC…',
  autoFocus = false,
  disabled = false,
  doctorId,
  locationId,
  testId = 'patient-picker',
  className = '',
  showLockedCard = true,
  showOpenHistory = true,
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [touched, setTouched] = useState(false);

  const abortRef = useRef(false);

  const openHistory = (patient, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!patient?.id) return;
    // Use the actual clinical-history route — `/admin/patients/:id` is NOT
    // a registered route and the catch-all redirects to `/` which on the
    // public domain forwards to `/booking`.
    navigate(`/admin/clinical-history?patient=${patient.id}`);
  };

  // Debounced search
  useEffect(() => {
    if (value || disabled) {
      setResults([]);
      return;
    }
    if (query.length < MIN_QUERY) {
      setResults([]);
      setLoading(false);
      return;
    }
    abortRef.current = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { search: query, page: 1, page_size: PAGE_SIZE };
        if (doctorId) params.doctor_id = doctorId;
        if (locationId) params.location_id = locationId;
        const r = await api.get('/patients', { params });
        if (abortRef.current) return;
        const list = r.data?.patients || (Array.isArray(r.data) ? r.data : []);
        setResults(list);
        setHighlight(0);
      } catch (_e) {
        if (!abortRef.current) setResults([]);
      } finally {
        if (!abortRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      abortRef.current = true;
      clearTimeout(t);
    };
  }, [query, value, disabled, doctorId, locationId]);

  const pick = (p) => {
    onChange?.(p);
    setQuery('');
    setResults([]);
    setTouched(true);
  };

  const clear = () => {
    onChange?.(null);
    setQuery('');
    setResults([]);
  };

  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[highlight];
      if (!target) return;
      // ⌘/Ctrl/Shift + Enter → fire the secondary action (e.g. crear cita)
      if ((e.metaKey || e.ctrlKey || e.shiftKey) && onSecondaryAction) {
        onSecondaryAction(target);
        return;
      }
      pick(target);
    } else if (e.key === 'Escape') {
      setResults([]);
    }
  };

  // Render locked card when a patient is selected
  if (value && showLockedCard) {
    return (
      <div
        className={`flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 ${className}`}
        data-testid={`${testId}-selected`}
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" data-testid={`${testId}-selected-name`}>
            {value.name}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {value.dni || 'sin DNI'} · NHC {value.nhc || '—'}
            {value.assigned_doctor_name ? ` · Dr. ${value.assigned_doctor_name}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {showOpenHistory && (
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={(e) => openHistory(value, e)}
              title="Abrir historia clínica"
              data-testid={`${testId}-open-history`}
              className="h-8 px-2"
            >
              <FileText size={14} />
            </Button>
          )}
          {!disabled && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clear}
              data-testid={`${testId}-clear`}
              type="button"
            >
              Cambiar
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="relative">
        <User
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <Input
          autoFocus={autoFocus}
          disabled={disabled}
          value={query}
          onChange={(e) => { setQuery(toTitleCaseNameLive(e.target.value)); setTouched(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="text-sm pl-8 pr-8"
          data-testid={`${testId}-search`}
        />
        {query && !loading && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            data-testid={`${testId}-search-clear`}
            aria-label="Limpiar búsqueda"
          >
            <X size={14} />
          </button>
        )}
        {loading && (
          <Loader2
            size={14}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"
          />
        )}
      </div>

      {!loading && touched && query.length >= MIN_QUERY && results.length === 0 && (
        <p
          className="text-xs text-gray-500 px-1"
          data-testid={`${testId}-empty`}
        >
          Ningún paciente encontrado.
        </p>
      )}

      {results.length > 0 && (
        <div
          className="border rounded-md max-h-60 overflow-y-auto divide-y bg-white shadow-sm"
          data-testid={`${testId}-results`}
        >
          {results.map((p, idx) => {
            const lastApt = formatShort(p.last_appointment_date);
            const lastSurg = formatShort(p.last_surgery_date);
            return (
              <div
                key={p.id}
                className={`flex items-stretch transition-colors ${
                  idx === highlight ? 'bg-brand-soft/40' : 'hover:bg-gray-50'
                }`}
                onMouseEnter={() => setHighlight(idx)}
              >
                <button
                  type="button"
                  className="flex-1 text-left px-3 py-2 text-sm min-w-0"
                  onClick={() => pick(p)}
                  data-testid={`${testId}-option-${p.id}`}
                >
                  <p className="font-medium truncate">{p.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {p.dni || 'sin DNI'} · NHC {p.nhc || '—'}
                    {p.assigned_doctor_name ? ` · Dr. ${p.assigned_doctor_name}` : ''}
                  </p>
                  {(lastApt || lastSurg) && (
                    <p
                      className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-2 truncate"
                      data-testid={`${testId}-option-history-meta-${p.id}`}
                    >
                      {lastApt && (
                        <span className="inline-flex items-center gap-1">
                          <CalendarClock size={10} />
                          {lastApt}
                        </span>
                      )}
                      {lastSurg && (
                        <span className="inline-flex items-center gap-1">
                          <Stethoscope size={10} />
                          {lastSurg}
                        </span>
                      )}
                    </p>
                  )}
                </button>
                {showOpenHistory && (
                  <button
                    type="button"
                    onClick={(e) => openHistory(p, e)}
                    className="px-3 flex items-center justify-center text-gray-400 hover:text-brand-navy hover:bg-brand-soft/40 border-l border-gray-100 transition-colors"
                    title="Abrir historia clínica en nueva navegación"
                    data-testid={`${testId}-option-history-${p.id}`}
                  >
                    <FileText size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
